'use client'

import { useEffect, useRef } from 'react'

/**
 * Calm "live" page background, behind all content:
 *  - washi-paper base colour with a fine paper grain
 *  - four large, very soft brand-colour lights drifting slowly (CSS animation, GPU only)
 *  - a faint seigaiha wave pattern that drifts sideways
 *  - a soft warm light that follows the mouse
 * No particles, nothing falling. Respects "reduce motion".
 */
export function LiveBackground() {
  const glow = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = glow.current
    if (!el) return
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return
    let x = window.innerWidth / 2
    let y = window.innerHeight / 3
    let tx = x
    let ty = y
    let raf = 0
    const loop = () => {
      x += (tx - x) * 0.08
      y += (ty - y) * 0.08
      el.style.transform = `translate3d(${x - 300}px, ${y - 300}px, 0)`
      raf = Math.abs(tx - x) + Math.abs(ty - y) > 0.5 ? requestAnimationFrame(loop) : 0
    }
    const onMove = (e: PointerEvent) => {
      tx = e.clientX
      ty = e.clientY
      el.style.opacity = '1'
      if (!raf) raf = requestAnimationFrame(loop)
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    return () => {
      window.removeEventListener('pointermove', onMove)
      cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <div aria-hidden className="ncp-live-bg">
      <div className="ncp-live-light ncp-live-light-1" />
      <div className="ncp-live-light ncp-live-light-2" />
      <div className="ncp-live-light ncp-live-light-3" />
      <div className="ncp-live-light ncp-live-light-4" />
      <div className="ncp-live-waves" />
      <div className="ncp-live-grain" />
      <div ref={glow} className="ncp-live-cursor" />
    </div>
  )
}
